"use client";

// Global error boundary — catches client-side render crashes that the root
// layout can't. Reports the crash to the server (which emails/alerts via
// notifyError) and gives the user a "Try again" button.
import { useEffect } from "react";
import {
  isChunkLoadError,
  recoverChunkLoadFailure,
} from "@/libs/chunk-load-recovery";
import { collectClientErrorEnv } from "@/libs/client-error-env";

export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    // Transient chunk-load failures (stale bundle after a deploy, or a flaky /
    // blocked connection) self-heal on refresh. Recover silently by reloading
    // once instead of showing a broken screen and alerting the owner. If we
    // already reloaded and it still failed, recoverChunkLoadFailure() returns
    // false and we fall through to normal reporting (genuine broken deploy).
    if (isChunkLoadError(error?.message, error?.digest)) {
      if (recoverChunkLoadFailure()) return;
    }

    fetch("/api/error-report", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        context: "client.global-error",
        message: error?.message || "Unknown client error",
        // Prefer the real stack; digest alone left non-digest errors (e.g. the
        // 2026-08 React #310 / null.get crashes) reporting with an empty stack,
        // making them impossible to localize.
        stack: error?.stack || error?.digest || "",
        // Translate/extension forensics (2026-09-14) — see libs/client-error-env.
        ...collectClientErrorEnv(),
      }),
    }).catch(() => {
      // Best-effort; never let reporting crash the error page.
    });
  }, [error]);

  return (
    // Keep the translate opt-out here too (see app/layout.tsx): this boundary
    // replaces the root layout's <html>, and a translate-caused crash that
    // resets back into an still-translated DOM would crash again.
    <html lang="en" translate="no" className="notranslate">
      <body>
        <main
          style={{
            minHeight: "100vh",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "1rem",
            fontFamily: "system-ui, sans-serif",
          }}
        >
          <div style={{ textAlign: "center", maxWidth: "28rem" }}>
            <h1 style={{ fontSize: "1.5rem", marginBottom: "0.5rem" }}>
              Something went wrong
            </h1>
            <p style={{ color: "#666", marginBottom: "1.5rem" }}>
              An unexpected error occurred. Our team has been notified. Try
              again, or reload the page.
            </p>
            <button
              onClick={reset}
              style={{
                padding: "0.5rem 1rem",
                background: "#000",
                color: "#fff",
                border: "none",
                borderRadius: "0.375rem",
                cursor: "pointer",
              }}
            >
              Try again
            </button>
          </div>
        </main>
      </body>
    </html>
  );
}
